import React from 'react'
import styled from 'styled-components'
import { ColumnProps } from 'antd/lib/table'
import { TagCP } from 'submodules/nerit-framework-ui/common/components/tag/TagCP'
import { ParameterGridResponseDTO, ParameterGridCellResponseDTO } from 'submodules/nerit-aquatec-sdk/services/parameter-control/dtos/responses/ParameterGridResponseDTO'
import { AnalysisStatusUtils } from 'modules/collection-performance/utils/AnalysisStatusUtils'

/** Um ponto (coluna) da grade do Controle de Parametros. */
export type ParameterLocationTP = {
    locationCode: number,
    locationName: string,
}

/** Uma linha da grade (um parametro) com as celulas indexadas por ponto. */
type ParameterRowTP = {
    key: number,
    fieldCode: number,
    fieldName: string,
    cellsByLocation: { [locationCode: number]: ParameterGridCellResponseDTO },
}

export type OnClickParameterCellTP = (location: ParameterLocationTP, cell: ParameterGridCellResponseDTO) => void

type TableStructureTP = {
    columns: Array<ColumnProps<ParameterRowTP>>,
    data: ParameterRowTP[],
}

/**
 * Monta a estrutura da grade parametro (linha) x ponto (coluna) do Controle de Parametros (Tela 3.3).
 */
export class ParameterControlTableUtils {

    static mountTableStructure(
        grid: ParameterGridResponseDTO,
        onClickCell: OnClickParameterCellTP,
    ): TableStructureTP {

        const columns: Array<ColumnProps<ParameterRowTP>> = [
            {
                title: 'Parâmetro',
                dataIndex: 'fieldName',
                className: 'fixedColumn',
                width: 240,
            },
            ...grid.locations.map((location) => ({
                title: location.locationName,
                key: String(location.locationCode),
                align: 'center' as const,
                width: 150,
                render: (text: string, row: ParameterRowTP) => {
                    const cell = row.cellsByLocation[location.locationCode]
                    if (!cell)
                        return null

                    return {
                        children: (
                            <CellWrapperSCP onClick={() => onClickCell(location, cell)}>
                                <TagCP
                                    content={AnalysisStatusUtils.getLabel(cell.status)}
                                    color={AnalysisStatusUtils.getColor(cell.status)}
                                    borderType={'circle'}
                                    small={true}
                                />
                                {!!cell.note && <NoteHintSCP title={cell.note}>obs.</NoteHintSCP>}
                            </CellWrapperSCP>
                        ),
                    }
                },
            })),
        ]

        // Reune a uniao de parametros (ordem de primeira aparicao) para as linhas.
        const fieldsMap = new Map<number, string>()
        grid.locations.forEach((location) => {
            location.cells.forEach((cell) => {
                if (!fieldsMap.has(cell.fieldCode))
                    fieldsMap.set(cell.fieldCode, cell.fieldName)
            })
        })

        const data: ParameterRowTP[] = Array.from(fieldsMap).map(([fieldCode, fieldName]) => {
            const cellsByLocation: { [locationCode: number]: ParameterGridCellResponseDTO } = {}
            grid.locations.forEach((location) => {
                const cell = location.cells.find((currentCell) => currentCell.fieldCode === fieldCode)
                if (!!cell)
                    cellsByLocation[location.locationCode] = cell
            })

            return { key: fieldCode, fieldCode, fieldName, cellsByLocation }
        })

        return { columns, data }
    }

}

const CellWrapperSCP = styled.div`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
`

const NoteHintSCP = styled.span`
    font-size: 0.65rem;
    color: #1890ff;
    text-transform: uppercase;
`
